import { MODALS_ENUM } from "@constants/Modals";
import { RootDispatch } from "@store";
import { logout } from "@store/auth/actions";
import clearAllCookies from "@utils/clearAllCookies";
import { dev } from "@utils/dev";
import { useToasts } from "@utils/hooks/useToasts";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { show } from "redux-modal";
import BaseRequest, {
  GetRequest,
  PostJsonRequest,
  PostRequest,
  PutRequest,
} from "./BaseRequest";
import httpService, { ErrorData } from "./HttpService";
import ServerErrorCodes from "./ServerErrorCodes";
import { BaseResponse } from "./responses/BaseResponse";
import useApiRoleGuard from "./useApiRoleGuard";

const SESSION_OVER: { RU: string; EN: string } = {
  RU: "Время аутентификации истекло",
  EN: "Your session is over",
};
const lang = (localStorage.getItem("lang") ||
  "RU") as keyof typeof SESSION_OVER;
export const MSG_SESSION_OVER = SESSION_OVER[lang];
//let tst = 0;

let twoFaOptions = {
  email: null,
  password: null,
};

export interface RequestOptions {
  isBlob?: boolean;
  onError?: (error: ErrorData) => void;
  onLoadingStatusChanged?: (isLoading: boolean) => void;
}

type EnqueueMethod = <R extends BaseResponse>(
  request: BaseRequest,
  options?: RequestOptions
) => Promise<R | void>;

interface IApiRequest {
  isLoading: boolean;
  enqueue: EnqueueMethod;
  enqueueSilent: EnqueueMethod;
  getImage(url: string): Promise<Blob>;
}

interface EnqueuedRequest {
  url: string;
  controller: AbortController;
}

export function testRequestEx<R extends BaseResponse>(
  request: BaseRequest | void,
  abortSignal?: AbortSignal,
  blob?: boolean
) {
  if (request instanceof GetRequest) {
    return httpService.get<R>(request.url, abortSignal, request.params);
  }
  if (request instanceof PostRequest) {
    return httpService.post<R>(request.url, request.params, abortSignal, blob);
  }
  if (request instanceof PutRequest) {
    return httpService.put<R>(request.url, request.params, abortSignal);
  }
  return Promise.reject(
    new ErrorData(ServerErrorCodes.Unknown, "Unknown request type")
  );
}

export default function useApiRequests(): IApiRequest {
  const { error } = useToasts();
  const dispatch = useDispatch<RootDispatch>();
  const [isLoading, setLoading] = useState<boolean>(false);
  const guard = useApiRoleGuard();

  const queue: EnqueuedRequest[] = [];

  useEffect(() => {
    return () => {
      queue.forEach((request) => {
        dev.log(`abort request: ${request.url}`);
        request.controller.abort();
      });
    };
  }, []);

  function makeRequest<R extends BaseResponse>(
    request: BaseRequest,
    abortSignal?: AbortSignal,
    blob?: boolean
  ) {
    if (request instanceof GetRequest) {
      return httpService.get<R>(request.url, abortSignal, request.params);
    }
    if (request instanceof PostJsonRequest) {
      return httpService.postJson<R>(
        request.url,
        request.params,
        abortSignal
      );
    }
    if (request instanceof PostRequest) {
      return httpService.post<R>(
        request.url,
        request.params,
        abortSignal,
        blob
      );
    }
    if (request instanceof PutRequest) {
      return httpService.put<R>(request.url, request.params, abortSignal);
    }
    return Promise.reject(
      new ErrorData(ServerErrorCodes.Unknown, "Unknown request type")
    );
  }

  function processError(e: any): void {
    if (e instanceof ErrorData) {
      switch (e.code) {
        // case 413: по идее не должно сюда дойти
        //   error(formatMessage({ id: "contracts.files.too_large" }));
        //   break;

        case ServerErrorCodes.Canceled:
          // show nothing
          break;
        case ServerErrorCodes.Unknown:
          if (e.status && e.status !== 200) {
            error(e.message);
          }
          break;
        case ServerErrorCodes.Need2FA:
          if (!twoFaOptions.email || !twoFaOptions.password) {
            console.error("No twoFaOptions (email or pasword)!");
          }
          dispatch(
            show(MODALS_ENUM.USE_TWO_FA_MODAL, {
              email: twoFaOptions.email,
              password: twoFaOptions.password,
            })
          );
          break;
        case ServerErrorCodes.NeedLogon:
          clearAllCookies();
          doLogout("ServerErrorCodes.NeedLogon");
          break;
        case ServerErrorCodes.PermissionDenied:
          error(e.message);
          // doLogout("ServerErrorCodes.PermissionDenied");
          break;
        default:
          error(e.message);
      }
    }
  }

  async function getImage(url: string): Promise<Blob> {
    const controller = new AbortController();
    queue.push({ url: url, controller });
    const response = await httpService.getBlob(url, controller.signal);
    return response;
  }

  // для запроса без стейтов
  async function enqueueSilent<R extends BaseResponse>(
    request: BaseRequest,
    options?: RequestOptions
  ): Promise<R | void> {
    if (!guard.allowURL(request.url)) {
      return;
    }
    const controller = new AbortController();
    queue.push({ url: request.url, controller });
    try {
      const response = (await makeRequest(request, controller.signal)) as R;
      return Promise.resolve(response);
    } catch (e: any) {
      if (e instanceof ErrorData) {
        if (e.code === 413) {
          return Promise.reject(e);
        }
        if (options && options.onError) {
          options.onError(e);
        } else {
          processError(e);
        }
        return Promise.resolve();
      }
      return Promise.reject(e);
    }
  }

  async function enqueue<R extends BaseResponse>(
    request: BaseRequest,
    options?: RequestOptions
  ): Promise<R | void> {
    if (!guard.allowURL(request.url)) {
      return;
    }
    setLoading(true);
    if (options && options.onLoadingStatusChanged) {
      options.onLoadingStatusChanged(true);
    }
    const controller = new AbortController();
    queue.push({ url: request.url, controller });
    try {
      const response = (await makeRequest(
        request,
        controller.signal,
        options?.isBlob
      )) as R;

      return Promise.resolve(response);
    } catch (e: any) {
      //console.log("enqueue error", e);
      if (e instanceof ErrorData) {
        // if (e.code === 413) {
        //   return Promise.reject(e);
        // }
        if (options && options.onError) {
          options.onError(e);
        } else {
          if (e.code === ServerErrorCodes.Need2FA) {
            twoFaOptions.email = request.params.email;
            twoFaOptions.password = request.params.password;
          }
          //dbg.trace(`API Error on URL ${request.url}, user: ${guard.email}, role: ${guard.role}, isAdmin: ${guard.isAdmin} \nERROR: ${e}`);
          processError(e);
        }
        return Promise.resolve();
      }
      return Promise.reject(e);
    } finally {
      setLoading(false);
      if (options && options.onLoadingStatusChanged) {
        options.onLoadingStatusChanged(false);
      }
    }
  }

  const doLogout = async (tag: string) => {
    //setTimeout(async () => {
    dispatch(logout(tag));
    try {
      await httpService.get("/logoff");
    } catch { }
    if (window.location.pathname !== "/login") {
      window.location.href = "/login";
    }
    //}, 100);
  };

  return {
    isLoading,
    enqueue,
    enqueueSilent,
    getImage,
  };
}
